import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CheckedInUser } from "src/components/CheckedIn/checkedInUser";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { ViewerMessage } from "src/components/CheckedIn/CheckedInContentStyles";
import { CompanyQuote, getCompanyQuotes } from "./quotesApi";
import { QuoteCard } from "./QuoteCard";
import { QuoteArticleView } from "./QuoteArticleView";
import { QuoteCarousel, QuotesRoot } from "./QuotesViewerStyles";
import { DefaultMyQuotesFeatureName } from "../Myquotes";

export { DefaultMyQuotesFeatureName as DefaultQuotesViewerName } from "../Myquotes";

export default function QuotesViewer({ user }: { user: CheckedInUser }): React.ReactElement {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const quoteId: string | null = params.get("quote");
    const articleId: string | null = params.get("article");
    const [quotes, setQuotes] = useState<CompanyQuote[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [retry, setRetry] = useState<number>(0);
    const carousel = useRef<HTMLDivElement>(null);
    const carouselOffset = useRef<number>(0);
    const name: string = user.customMyQuotesName || DefaultMyQuotesFeatureName;
    const selected: CompanyQuote | undefined = quotes.find(quote => quote.uuid === quoteId);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        void getCompanyQuotes(controller.signal).then(data => {
            if (!controller.signal.aborted) {
                setQuotes(data);
                setLoading(false);
            }
        }).catch(() => {
            if (!controller.signal.aborted) {
                setError(true);
                setLoading(false);
            }
        });
        return () => controller.abort();
    }, [retry]);

    useEffect(() => {
        window.scrollTo({ top: 0 });
        if (!quoteId && !articleId && carousel.current) {
            carousel.current.scrollLeft = carouselOffset.current;
        }
    }, [quoteId, articleId]);

    const closeArticle = (): void => {
        history.push(quoteId ? `/myquotes?quote=${encodeURIComponent(quoteId)}` : "/myquotes");
    };
    const openArticle = (quote: CompanyQuote, full: boolean): void => {
        if (quote.article) {
            history.push(`/myquotes?${full ? `quote=${encodeURIComponent(quote.uuid)}&` : ""}article=${encodeURIComponent(quote.article.uuid)}`);
        }
    };
    const close = (): void => history.push("/myquotes");

    return <QuotesRoot aria-label={name}>
        {articleId ? <QuoteArticleView uuid={articleId} onClose={closeArticle} />
            : loading ? <ViewerMessage role="status">Loading {name}…</ViewerMessage>
                : error ? <ViewerMessage $error role="alert">We couldn’t load {name}.
                    <div><CheckedInAction type="button" onClick={() => setRetry(value => value + 1)}>Try again</CheckedInAction></div>
                </ViewerMessage> : quoteId ? selected ? <QuoteCard key={selected.uuid} quote={selected} full
                    onOpen={() => undefined} onArticle={() => openArticle(selected, true)} onClose={close} />
                    : <ViewerMessage role="status">This quote is no longer available.
                        <div><CheckedInAction type="button" onClick={close}>Back to {name}</CheckedInAction></div>
                    </ViewerMessage> : quotes.length > 0 ? <QuoteCarousel ref={carousel} role="region"
                    aria-label={`${name} carousel`} onScroll={event => { carouselOffset.current = event.currentTarget.scrollLeft; }}>
                    {quotes.map(quote => <QuoteCard key={quote.uuid} quote={quote} full={false}
                        onOpen={() => history.push(`/myquotes?quote=${encodeURIComponent(quote.uuid)}`)}
                        onArticle={() => openArticle(quote, false)} onClose={close} />)}
                </QuoteCarousel> : <ViewerMessage>No quotes yet. Please check back soon.</ViewerMessage>}
    </QuotesRoot>;
}
